import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';

/**
 * Determines whether the Contact Collection Message feature is enabled.
 *
 * Evaluates two conditions:
 * - `enableContactCollectionMessage` (Amplitude) — global feature flag
 * - `contactCollectorOptinOverride` (event feature flag) — per-event override set via Event Service Center
 *
 * Logic: `amplitudeFlag === 'on' || contactCollectorOptinOverride`
 *
 * Note: Most call sites also check `hasMessagingPurchase` separately via OR logic.
 * This hook intentionally does NOT include purchase logic to maintain flexibility
 * at call sites that need different business logic patterns.
 *
 * @returns `[isEnabled, isLoading]` tuple
 */
export const useIsContactCollectionMessageEnabled = (): [isEnabled: boolean, isLoading: boolean] => {
  const { value: amplitudeValue, loading: amplitudeLoading } = useFeatureValue('enableContactCollectionMessage');
  const [isOverrideEnabled, overrideLoading] = useIsFeatureEnabled({ eventFeatureFlagKey: 'contactCollectorOptinOverride' });

  const isLoading = amplitudeLoading || overrideLoading;
  const isEnabled = amplitudeValue === 'on' || isOverrideEnabled;

  return [isEnabled, isLoading];
};
